/* eslint-disable max-lines-per-function */
import {debounced} from '@mvps-genesis/utilities';
import 'extensions/proforma/components/x-signature-capture';
import {proformaStore} from 'extensions/proforma/data/proforma-store';
import 'extensions/ui-components/x-tooltip';
import {LitElement, html, nothing} from 'lit';
import {createRef, ref} from 'lit/directives/ref.js';
import styles from './x-proforma.component.scss?as=stylesheet';

export class ProForma extends LitElement {

	static styles = [styles];

	static get properties () {
		return {
			checkboxValid: {
				type: Boolean
			},
			cta: {
				type: String
			},
			modify: {
				type: Boolean
			},
			proformaState: {
				attribute: 'proforma-state',
				type: Object
			},
			signatureValid: {
				type: Boolean
			},
			state: {
				state: true,
				type: Object
			},
			stateLoaded: {
				type: Boolean
			},
			step: {
				type: Number
			},
			submitting: {
				type: Boolean
			},
			url: {
				type: String
			},
			valid: {
				type: Boolean
			}
		};
	}

	form = createRef();

	agreementCheckbox = createRef();

	constructor () {
		super();

		this.cta = '';
		this.modify = 0;
		this.proformaState = {};
		this.signatureValid = false;
		this.state = proformaStore.state;
		this.stateLoaded = false;
		this.step = 0;
		this.submitting = false;
		this.url = '';
		this.valid = false;
		this.validationState = {};

		this.handlePopState = debounced(200, this._handlePopState.bind(this));
	}

	async connectedCallback () {
		super.connectedCallback();

		this.addEventListener('option_click', (e) => {
			this.updateState(e);
		});

		this.addEventListener('section_render', (e) => {
			const code = e.detail.section;
			const required = e.detail.required;

			this.validationState[code] = required;
			this.requestUpdate();
		});

		this.addEventListener('terms_valid', (e) => {
			const code = e.detail.section;
			delete this.validationState[code];
			this.requestUpdate();
		});

		window.addEventListener('popstate', this.handlePopState);

		if (this.proformaState && Object.keys(this.proformaState).length) {
			await this._getStateFromData();
		} else {
			await this._getStateFromId();
		}

		this.stateLoaded = true;

		this.valid = this.determineValidity;

		this.updateCheckoutStepsURLs();

		if (this.step === 4) {
			const paymentForms = document.querySelectorAll('#js-opay-form, #js-proforma-modify-form');
			for (const paymentForm of paymentForms) {
				paymentForm.addEventListener('submit', this.handlePaymentFormSubmit.bind(this));
			}

			this.addPaymentTooltip();

			const buttons = document.getElementsByClassName('js-checkout-button');
			for (const button of buttons) {
				const observer = new MutationObserver(() => {
					this.handleValidUpdate();
				});

				observer.observe(button, {
					attributes: true
				});
			}

		}
	}

	disconnectedCallback () {
		window.removeEventListener('popstate', this.handlePopState);
	}

	updated (changedProperties) {
		if (changedProperties.has('valid')) {
			this.handleValidUpdate();
		}

		this.valid = this.determineValidity;
	}

	calculateTotal () {
		let total = 0;
		for (const section in this.state) {
			let sectionTotal = 0;
			if (section !== 'id' && section !== 'signature') {
				for (const prod of this.state[section]) {
					if (prod.productPrice && prod.productPrice !== '') {
						let prodTotal = prod.productPrice * prod.productQuantity;
						sectionTotal += prodTotal;
					}
				}
			}
			total += sectionTotal;
		};

		return total;
	}

	// Render the UI as a function of component store
	render () {
		if (!this.stateLoaded) {
			return nothing;
		}

		return html`
			<div class="x-proforma">
				<slot></slot>
				${this.renderStep()}
			</div>
		`;
	}

	renderStep () {
		if (this.step === 4) {
			return html`
				${this.renderTermsAndConditions()}
				<div class="signature">
					<div class="signature-title">Signature</div>
					<div class="signature-capture">
						<x-signature-capture id="js-signature" @endStroke="${this.handleSignatureChange}"></x-signature-capture>
						<div class="signature-capture-footer"></div>
					</div>
				</div>
			`;
		}

		return html`
				<form method="POST" action="${this.formattedURL}" @submit="${this.handleSubmit}" ${ref(this.form)} class="x-proforma-form">
					${this.renderHiddenInputs()}
					${this.renderInstallationDateSelector()}
					<div class="button-container">
						<button class="back-button" @click="${(e) => this.handleBackButton(e)}">Back</button>
						<div class="cta-wrapper">
							<button type="submit" class="cta-button" ?disabled="${this.submitting || !this.valid}">${this.submitting ? 'Loading...' : this.cta}</button>
						</div>
					</div>
				</form>
		`;
	}

	renderInstallationDateSelector () {
		if (this.step !== 3) {
			return nothing;
		}

		return html`
			<x-installation-date-selector @change="${this.handleInstallationDateChange}" ?modify="${this.modify}" selected="${this.state?.installationDate}" notes="${this.state?.installationNotes}"></x-installation-date-selector>
		`;
	}

	renderTermsAndConditions () {
		return html`
			<div class="terms-and-conditions">
				<div class="terms-and-conditions-title">Terms and Conditions</div>
				<div class="terms-and-conditions-wrapper">
					<slot name="terms-and-conditions"></slot>
				</div>
			</div>

			<div class="agreement">
				<label for="agreement" class="checkbox-label">
					<input type="checkbox" id="agreement" name="agreement" class="checkbox-input" ${ref(this.agreementCheckbox)} @change="${this.handleAgreementCheckboxChange}" />
					<span class="checkbox-caption">I agree to the terms and conditions</span>
				</label>
			</div>
		`;
	}

	get formattedURL () {
		if (!this.state?.id) {
			return this.url;
		}

		if (!this.url.includes('https')) {
			this.url = `https:${this.url}`;
		}

		const url = new URL(this.url);
		url.searchParams.append('state_id', this.state.id);

		return url;
	}

	get determineValidity () {
		switch (this.step) {
			default:
			case 0:
			case 1: return true;
			case 2: return this.determineOptionsValidity;
			case 3: return this.determineInstallationValidity;
			case 4: return this.determinePaymentValidity;
		}
	}

	get determineOptionsValidity () {
		let requiredSections = Object.keys(this.validationState).filter(key => this.validationState[key] === 1);
		let isValid = requiredSections.every(key => {
			return this.state[key] && Array.isArray(this.state[key]) && this.state[key].length > 0;
		});

		return isValid;
	}

	get determinePaymentValidity () {
		if (!this.checkboxValid || !this.signatureValid) {
			return false;
		}

		return true;
	}

	get determineInstallationValidity () {
		if (!this.state?.installationDate) {
			return false;
		}

		return true;
	}

	renderHiddenInputs () {
		if (this.step === 3) {
			return html`
				<input type="hidden" name="Action" value="SHIP,PSHP,CTAX" />
				<input type="hidden" name="ShippingMethod" value="flatrate:ProForma Shipping" />
				<input type="hidden" name="PaymentMethod" value="${window.mivaJS?.Mivapay_Enabled ? 'mivapay:' : 'braintree:Visa' }" />
				<input type="hidden" name="Proforma_Installation_Date" value="${this.state?.installationDate}" />
				<input type="hidden" name="Proforma_State" value="${JSON.stringify(this.state)}" />
				<input type="hidden" name="Proforma_Installation_Notes" value="${this.state?.installationNotes}" />
			`;
		}

		return nothing;
	}

	updateState (e) {
		const {section, productPrice, productQuantity, productCode, sectionType, code} = e.detail;
		let copy = JSON.parse(JSON.stringify(this.state));

		if (!(section in copy)) {
			copy[section] = [];
		}

		if (copy[section].some(el => el.code === code)) {
			copy[section] = copy[section].filter((product) => product.code !== code);
			if (sectionType === 'option_with_quantity' && productQuantity !== 0) {
				copy[section].push({
					code,
					productCode,
					productPrice,
					productQuantity
				});
			}
		} else {
			if (!e.detail.multiSelect) {
				copy[section] = [];
			}
			copy[section].push({
				code,
				productCode,
				productPrice,
				productQuantity
			});
		}

		this.state = proformaStore.set(copy);
		const url = new URL(window.location.href);
		url.searchParams.set('state_id', this.state.id);
		window.history.replaceState(this.state.id, '', url.toString());

		window.dispatchEvent(new CustomEvent('update_total', {detail: {total: this.calculateTotal()}}));

		this.updateCheckoutStepsURLs();
	}

	async submitAddToCart () {
		const data = new FormData();
		let i = 1;

		for (const products of Object.values(this.state)) {
			for (const product of products) {
				if (!product.productPrice) {
					continue;
				}

				const prefix = `Products[${i}]`;
				data.append(`${prefix}:code`, product.productCode);
				data.append(`${prefix}:quantity`, product.productQuantity);

				i++;
			}
		}

		if (i === 1) {
			return true;
		}

		data.append('Action', 'ADPM');
		data.append('PROFORMA_BASKET', 1);

		const response = await fetch(window.mivaJS.Basket_Url, {
			body: data,
			method: 'POST'
		});

		if (!response.ok) {
			return false;
		}

		this.sendGTMEvent();

		return true;
	}

	_handlePopState () {
		this._getStateFromId();
	}

	async _getStateFromId () {
		const url = new URL(window.location.href);
		const stateId = url.searchParams.get('state_id');

		this.state = await proformaStore.get(stateId);

		this.updateCheckoutStepsURLs();
	}

	async _getStateFromData () {
		const data = this.proformaState;
		this.state = await proformaStore.set(data);

		this.updateCheckoutStepsURLs();
	}

	async handleSubmit (e) {
		this.submitting = true;

		if (this.step === 3) {
			e.preventDefault();
			await this.submitAddToCart();

			this.form.value.submit();
		}
	}

	handleInstallationDateChange (e) {
		let copy = JSON.parse(JSON.stringify(this.state));
		copy.installationNotes = e.detail?.notes;
		copy.installationDate = e.detail?.installationDate;
		this.state = proformaStore.set(copy);
	}

	handleAgreementCheckboxChange () {
		this.checkboxValid = this.agreementCheckbox?.value?.checked;
		this.updatePaymentValidationErrors();
	}

	handleSignatureChange () {
		this.signatureValid = 1;
		this.updatePaymentValidationErrors();
	}

	handleValidUpdate () {
		const buttons = document.getElementsByClassName('js-checkout-button');

		if (!buttons.length) {
			return;
		}

		for (const button of buttons) {
			if (button.disabled === !this.valid) {
				continue;
			}

			button.disabled = !this.valid;
		}

		this.updatePaymentValidationErrors();
	}

	updatePaymentValidationErrors () {
		const tooltipElements = document.getElementsByClassName('js-payment-tooltip');
		const tooltipContentElements = document.getElementsByClassName('js-payment-tooltip-content');

		for (const tooltipElement of tooltipElements) {
			tooltipElement.disabled = this.valid;
		}

		for (const tooltipContentElement of tooltipContentElements) {
			tooltipContentElement.innerHTML = '';
			if (!this.checkboxValid) {
				tooltipContentElement.innerHTML = 'Please agree to the terms and conditions.';
			} else if (!this.signatureValid) {
				tooltipContentElement.innerHTML = 'Please add your signature.';
			}
		}
	}

	updateCheckoutStepsURLs () {
		if (!this.state?.id) {
			return;
		}

		const links = document.getElementsByClassName('js-proforma-state-link');

		for (const link of links) {
			const url = new URL(link.href);
			url.searchParams.delete('state_id');
			url.searchParams.append('state_id', this.state.id);
			link.href = url.href;
		}
	}

	handlePaymentFormSubmit () {
		if (this.step === 4) {
			const signature = this.renderRoot?.querySelector('#js-signature');
			const signatureElement = document.getElementById('proforma-signature');
			if (signatureElement) {
				signatureElement.value = signature.value;
			}
		}
	}

	handleBackButton (e) {
		e.preventDefault();
		history.back();
	}

	addPaymentTooltip () {
		const buttons = document.getElementsByClassName('js-checkout-button');

		for (const button of buttons) {
			const parent = button.parentNode;
			var wrapper = document.createElement('x-tooltip');
			wrapper.classList.add('js-payment-tooltip');

			const tooltipContent = document.createElement('div');
			tooltipContent.setAttribute('slot', 'content');
			tooltipContent.classList.add('js-payment-tooltip-content');

			if (!this.checkboxValid) {
				tooltipContent.innerHTML = 'Please agree to the terms and conditions.';
			} else if (!this.signatureValid) {
				tooltipContent.innerHTML = 'Please add your signature.';
			}

			wrapper.appendChild(tooltipContent);
			parent.replaceChild(wrapper, button);
			wrapper.appendChild(button);
		}
	}

	sendGTMEvent () {
		const gtmProducts = [];
		for (const products of Object.values(this.state)) {
			for (const product of products) {
				gtmProducts.push({
					/* eslint-disable camelcase */
					item_id: product.code,
					item_list_id: 'proforma',
					item_list_name: 'Proforma',
					item_name: product.name,
					/* eslint-enable camelcase */
					price: product.price,
					quantity: product.quantity || 1
				});
			}
		}

		if (gtmProducts.length) {
			window.dataLayer.push({
				ecommerce: null
			});

			window.dataLayer.push({
				ecommerce: {
					add: {
						products: gtmProducts
					},
					currencyCode: window.mivaJS?.Currency_Code
				},
				event: 'addToCart'
			});
		}
	}
}
customElements.define('x-proforma', ProForma);
